/**
 * Execution Identity Selector Component
 *
 * Reusable component for selecting execution identities with session management
 */

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { RouterOutputs } from '@/types/trpc'
import { Plus } from 'lucide-react'
import { toast } from 'sonner'
import { trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'
import { Button } from '@deepguide-ai/ui/button'
import { Badge } from '@deepguide-ai/ui/badge'
import { SearchableSelect } from '@deepguide-ai/ui/combobox'
import { Label } from '@deepguide-ai/ui/label'
// cspell:disable-next-line
import { BrowserProfileDialog } from './BrowserDaialog'
import { CreateBrowserProfileDialog } from './CreateBrowserProfileDialog'

type BrowserProfile = RouterOutputs['browserProfile']['list'][number]

interface BrowserProfileSelectorProps {
  selectedProfileId: string
  onProfileChange: (profileId: string, steelProfileId: string) => void
  showFreshProfile?: boolean
  label?: string
  description?: string
}

/** One identity row: status dot (green=logged in / gray=logged out) + name +
 *  · role + a "Logged out" badge. Shared by the Select trigger and each item. */
function IdentityRow({ profile }: { profile: BrowserProfile }) {
  return (
    <span className='flex items-center gap-2 truncate'>
      <span
        className={cn(
          'h-2 w-2 shrink-0 rounded-full',
          profile.isValid ? 'bg-green-500' : 'bg-gray-400'
        )}
      />
      <span className='truncate'>{profile.name}</span>
      {profile.profileType && (
        <span className='text-muted-foreground shrink-0'>
          · {profile.profileType}
        </span>
      )}
      {!profile.isValid && (
        <Badge
          variant='outline'
          className='shrink-0 px-1 py-0 text-[10px] font-normal'
        >
          Logged out
        </Badge>
      )}
    </span>
  )
}

export function BrowserProfileSelector({
  selectedProfileId,
  onProfileChange,
  showFreshProfile = true,
  label = 'Select Identity',
  description = 'Choose an identity for workflow execution',
}: BrowserProfileSelectorProps) {
  const queryClient = useQueryClient()
  const [profileDialogOpen, setProfileDialogOpen] = useState(false)
  const [addProfileOpen, setAddProfileOpen] = useState(false)
  const [viewingProfileId, setViewingProfileId] = useState<string | null>(null)

  const [sessionUrl, setSessionUrl] = useState<string | null>(null)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [sessionLoading, setSessionLoading] = useState(false)
  const [sessionReleased, setSessionReleased] = useState(false)

  // Fetch browser profiles
  const profilesQuery = useQuery(
    trpc.browserProfile.list.queryOptions({
      includeCreator: false,
      includeCounts: false,
    })
  )

  const browserProfiles: BrowserProfile[] = profilesQuery.data || []

  // Browser profile session mutations
  const startSessionMutation = useMutation({
    ...trpc.browserProfile.startSession.mutationOptions(),
    onSuccess: (data) => {
      setSessionLoading(false)
      setSessionId(data.sessionId)
      setSessionUrl(data.sessionViewerUrl)
      setSessionReleased(false)
    },
    onError: (error) => {
      setSessionLoading(false)
      setSessionUrl(null)
      setSessionId(null)
      toast.error(`Failed to create session: ${error.message || 'Unknown error'}`)
    },
  })

  const discardSessionMutation = useMutation({
    ...trpc.browserProfile.discardSession.mutationOptions(),
    onSuccess: () => {
      setSessionReleased(true)
      setSessionUrl(null)
      setSessionId(null)
    },
    onError: (error) => {
      toast.error(`Failed to close session: ${error.message || 'Unknown error'}`)
    },
  })

  const loginCompletionMutation = useMutation({
    ...trpc.browserProfile.setLoginCompletion.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: trpc.browserProfile.list.queryKey(),
      })
    },
    onError: (error) => {
      toast.error(`Failed to update login status: ${error.message || 'Unknown error'}`)
    },
  })

  const handleProfileChange = (profileId: string) => {
    if (profileId === '__fresh__') {
      onProfileChange(profileId, '')
      return
    }
    const selectedProfile = browserProfiles.find((profile) => profile.id === profileId)
    onProfileChange(profileId, selectedProfile?.steelProfileId ?? '')
  }

  const handleViewProfile = (profileId: string) => {
    if (profileId && profileId !== '__fresh__') {
      setViewingProfileId(profileId)
      setProfileDialogOpen(true)
      setSessionLoading(true)
      setSessionUrl(null)
      setSessionId(null)
      setSessionReleased(false)
      startSessionMutation.mutate({ id: profileId })
    }
  }

  const handleDialogChange = (open: boolean) => {
    setProfileDialogOpen(open)
    if (!open) {
      if (viewingProfileId && sessionId && !sessionReleased) {
        void handleDiscardActiveSession()
      }
      setSessionUrl(null)
      setSessionId(null)
      setSessionReleased(false)
      setSessionLoading(false)
      setViewingProfileId(null)
    }
  }

  const handleLogInAgain = async () => {
    if (!viewingProfileId) {
      return
    }
    try {
      await handleDiscardActiveSession()
      setSessionLoading(true)
      setSessionUrl(null)
      setSessionId(null)
      setSessionReleased(false)
      startSessionMutation.mutate({ id: viewingProfileId })
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to restart session')
    }
  }

  const handleDiscardActiveSession = async () => {
    if (!sessionId || sessionReleased || !viewingProfileId || discardSessionMutation.isPending) {
      return
    }
    await discardSessionMutation.mutateAsync({ profileId: viewingProfileId })
  }

  const handleLoginCompletionChoice = async (hasLoginInfo: boolean, sessionIdFromDialog: string | null) => {
    if (!viewingProfileId) {
      return
    }

    try {
      await loginCompletionMutation.mutateAsync({
        profileId: viewingProfileId,
        hasLoginInfo,
        sessionId: sessionIdFromDialog || undefined,
      })

      if (hasLoginInfo && sessionIdFromDialog) {
        toast.success('Session saved successfully')
      }

      await queryClient.invalidateQueries({
        queryKey: trpc.browserProfile.list.queryKey(),
      })

      // Discard the session after login completion is recorded
      // This releases the Steel session since we either saved it or marked profile as not signed in
      if (sessionIdFromDialog && !sessionReleased) {
        await handleDiscardActiveSession()
      }
    } catch {
      // Toast handled in onError
    }
  }

  const activeProfile = browserProfiles.find((p) => p.id === selectedProfileId)
  const isFresh =
    selectedProfileId === '__fresh__' || (!selectedProfileId && showFreshProfile)

  return (
    <>
      <div className='space-y-3'>
        <div className='space-y-1'>
          <Label className='text-sm font-semibold text-text-secondary'>
            {label}
          </Label>
          <p className='text-muted-foreground text-xs'>{description}</p>
        </div>

        <div className='flex items-center gap-2'>
          <SearchableSelect
            options={[
              ...(showFreshProfile
                ? [{ value: '__fresh__', label: 'No logged-in user' }]
                : []),
              ...browserProfiles.map((profile) => ({
                value: profile.id,
                label: `${profile.name}${profile.profileType ? ` · ${profile.profileType}` : ''}`,
              })),
              { value: '__add__', label: 'Add new identity' },
            ]}
            value={selectedProfileId || (showFreshProfile ? '__fresh__' : '')}
            onValueChange={(value) => {
              if (value === '__add__') {
                setAddProfileOpen(true)
                return
              }
              handleProfileChange(value)
            }}
            placeholder='Select identity…'
            searchPlaceholder='Search identities...'
            triggerProps={{ className: 'h-9 flex-1 rounded-md text-[12px]' }}
            renderValue={() =>
              isFresh ? (
                <span className='flex items-center gap-2 truncate'>
                  <span className='h-2 w-2 shrink-0 rounded-full bg-gray-300' />
                  No logged-in user
                </span>
              ) : activeProfile ? (
                <IdentityRow profile={activeProfile} />
              ) : (
                <span className='text-muted-foreground'>Select identity…</span>
              )
            }
            renderOption={(option) => {
              if (option.value === '__fresh__') {
                return (
                  <span className='flex items-center gap-2'>
                    <span className='h-2 w-2 shrink-0 rounded-full bg-gray-300' />
                    <span className='flex flex-col'>
                      <span>No logged-in user</span>
                      <span className='text-muted-foreground text-[11px]'>
                        Start without a logged-in identity
                      </span>
                    </span>
                  </span>
                )
              }
              if (option.value === '__add__') {
                return (
                  <span className='flex items-center gap-2'>
                  <Plus className='h-3.5 w-3.5' />
                  Add new identity
                  </span>
                )
              }
              const profile = browserProfiles.find(
                (candidate) => candidate.id === option.value
              )
              return profile ? <IdentityRow profile={profile} /> : option.label
            }}
          />

          {!isFresh && activeProfile && (
            <Button
              type='button'
              variant='outline'
              size='sm'
              className='h-9 shrink-0'
              onClick={() => handleViewProfile(selectedProfileId)}
            >
              Check session
            </Button>
          )}
        </div>
      </div>

      {/* Browser Profile Dialog */}
      {viewingProfileId && viewingProfileId !== '__fresh__' && (
        <BrowserProfileDialog
          open={profileDialogOpen}
          onOpenChange={handleDialogChange}
          profile={browserProfiles.find((p) => p.id === viewingProfileId) || null}
          sessionUrl={sessionUrl}
          sessionLoading={sessionLoading}
          sessionId={sessionId}
          onLogInAgain={handleLogInAgain}
          isLoggingInAgain={startSessionMutation.isPending}
          onReleaseSession={handleDiscardActiveSession}
          onLoginCompletionChoice={handleLoginCompletionChoice}
          isSettingLoginCompletion={loginCompletionMutation.isPending}
        />
      )}

      {/* Create Browser Profile Dialog */}
      <CreateBrowserProfileDialog
        open={addProfileOpen}
        onOpenChange={setAddProfileOpen}
        onSuccess={(profile) => {
          if (profile?.id) {
            handleProfileChange(profile.id)
          }
          queryClient.invalidateQueries({
            queryKey: trpc.browserProfile.list.queryKey(),
          })
          setAddProfileOpen(false)
        }}
      />
    </>
  )
}
